/**
 * Peças da carroceria — fonte única da taxonomia de funilaria (F3.1).
 *
 * É a unidade de trabalho da funilaria, como o sistema (freio, motor) é a da
 * mecânica: a avaliação de dano fotografa por peça (F3.2), o orçamento agrupa
 * por peça (F3.3, `item.panel` de D4), a marcação e o antes/depois apontam para
 * uma peça, e o laudo de sinistro (Fase 4) lê a mesma lista. Nenhum app define
 * peça localmente — a mesma disciplina de `modules.ts` e da Regra 12.
 *
 * **A chave é contrato gravado.** Ela vai para o `item_ref` da foto
 * (`panel:<chave>`, D13) e para o item da OS. Trocar o texto de uma peça é
 * trocar o rótulo, nunca a chave; peça nova entra no fim da sua região e nunca
 * reaproveita chave de outra. Por isso as chaves são `snake_case` ASCII: viajam
 * em `item_ref`, em JSON do sync offline e em nome de arquivo baixado sem
 * escapar nada.
 *
 * **Lado.** Direito e esquerdo são de quem está sentado no banco do motorista,
 * olhando para a frente — a convenção das seguradoras e do mercado de peças
 * ("para-lama dianteiro esquerdo" é o do lado do motorista). A foto de frente
 * do checklist inverte isso para quem olha o carro, e é exatamente o erro que
 * a convenção existe para evitar: peça trocada do lado errado.
 *
 * Sufixos de posição, sempre os mesmos:
 *   - `_dd` `_de` `_td` `_te` — dianteira/traseira + direita/esquerda
 *     (portas, para-lamas, laterais);
 *   - `_ld` `_le` — lado direito/esquerdo, para peça que só tem lado
 *     (retrovisor, farol, coluna, caixa de ar).
 * Sem essa regra, `porta_de` é ambíguo ("direita"? "dianteira esquerda"?) —
 * e foi por essa ambiguidade que a foto de peça ficou com rótulo genérico até
 * esta lista existir.
 *
 * Ver `docs/ref/mustauto_modulos_verticais_plano.md` (F3.1, D4, D13).
 */

/** Regiões do carro, na ordem em que a avaliação percorre o veículo. */
export const VEHICLE_PANEL_REGIONS = ['frente', 'lado_esquerdo', 'lado_direito', 'traseira', 'teto_estrutura'] as const;

export type VehiclePanelRegion = (typeof VEHICLE_PANEL_REGIONS)[number];

export const VEHICLE_PANEL_REGION_LABELS: Record<VehiclePanelRegion, string> = {
  frente: 'Frente',
  lado_esquerdo: 'Lado esquerdo',
  lado_direito: 'Lado direito',
  traseira: 'Traseira',
  teto_estrutura: 'Teto e estrutura',
};

/**
 * As peças, agrupadas por região e em ordem de frente para trás dentro de
 * cada uma. A ordem é a que a tela mostra.
 */
export const VEHICLE_PANELS = {
  frente: [
    'capo',
    'para_choque_dianteiro',
    'farol_le',
    'farol_ld',
    'longarina_dianteira',
  ],
  lado_esquerdo: [
    'para_lama_de',
    'retrovisor_le',
    'porta_de',
    'porta_te',
    'coluna_le',
    'caixa_ar_le',
    'lateral_te',
  ],
  lado_direito: [
    'para_lama_dd',
    'retrovisor_ld',
    'porta_dd',
    'porta_td',
    'coluna_ld',
    'caixa_ar_ld',
    'lateral_td',
  ],
  traseira: [
    'tampa_traseira',
    'para_choque_traseiro',
    'lanterna_le',
    'lanterna_ld',
    'longarina_traseira',
  ],
  teto_estrutura: ['teto', 'assoalho'],
} as const satisfies Record<VehiclePanelRegion, readonly string[]>;

export type VehiclePanel = (typeof VEHICLE_PANELS)[VehiclePanelRegion][number];

/** Todas as chaves, na ordem das regiões. */
export const VEHICLE_PANEL_KEYS: readonly VehiclePanel[] = VEHICLE_PANEL_REGIONS.flatMap(
  (region) => VEHICLE_PANELS[region],
);

const PANEL_LABELS: Record<VehiclePanel, string> = {
  capo: 'Capô',
  para_choque_dianteiro: 'Para-choque dianteiro',
  farol_le: 'Farol esquerdo',
  farol_ld: 'Farol direito',
  longarina_dianteira: 'Longarina dianteira',

  para_lama_de: 'Para-lama dianteiro esquerdo',
  retrovisor_le: 'Retrovisor esquerdo',
  porta_de: 'Porta dianteira esquerda',
  porta_te: 'Porta traseira esquerda',
  coluna_le: 'Coluna esquerda',
  caixa_ar_le: 'Caixa de ar esquerda',
  lateral_te: 'Lateral traseira esquerda',

  para_lama_dd: 'Para-lama dianteiro direito',
  retrovisor_ld: 'Retrovisor direito',
  porta_dd: 'Porta dianteira direita',
  porta_td: 'Porta traseira direita',
  coluna_ld: 'Coluna direita',
  caixa_ar_ld: 'Caixa de ar direita',
  lateral_td: 'Lateral traseira direita',

  tampa_traseira: 'Tampa traseira',
  para_choque_traseiro: 'Para-choque traseiro',
  lanterna_le: 'Lanterna esquerda',
  lanterna_ld: 'Lanterna direita',
  longarina_traseira: 'Longarina traseira',

  teto: 'Teto',
  assoalho: 'Assoalho',
};

const PANEL_REGION: ReadonlyMap<string, VehiclePanelRegion> = new Map(
  VEHICLE_PANEL_REGIONS.flatMap((region) => VEHICLE_PANELS[region].map((panel) => [panel, region] as const)),
);

export function isVehiclePanel(value: unknown): value is VehiclePanel {
  return typeof value === 'string' && PANEL_REGION.has(value);
}

/**
 * Nome da peça, ou `null` para chave desconhecida — nunca a chave crua. Chave
 * desconhecida é a gravada por uma versão que conhecia outra lista; quem
 * mostra decide o rótulo genérico, como faz `panelPhotoLabel` em
 * @mustauto/utils.
 */
export function getVehiclePanelLabel(panel: string): string | null {
  return isVehiclePanel(panel) ? PANEL_LABELS[panel] : null;
}

/** Região da peça, ou `null` para chave desconhecida. */
export function getVehiclePanelRegion(panel: string): VehiclePanelRegion | null {
  return PANEL_REGION.get(panel) ?? null;
}

/* ==========================================================================
 * Serviço na peça (F3.2) — o `operation` de D4
 * ========================================================================== */

/**
 * O que a funilaria faz na peça, na ordem em que a avaliação oferece. Chave
 * gravada no item da OS (`item.operation`), como a peça (`item.panel`); trocar
 * o texto é trocar o rótulo, nunca a chave.
 *
 * Uma peça pode ter mais de um: o para-lama amassado é reparado E pintado.
 * `polir` (D4) fica para o módulo Estética.
 */
export const PANEL_OPERATIONS = ['reparar', 'substituir', 'pintar'] as const;

export type PanelOperation = (typeof PANEL_OPERATIONS)[number];

const PANEL_OPERATION_LABELS: Record<PanelOperation, string> = {
  reparar: 'Reparar',
  substituir: 'Substituir',
  pintar: 'Pintar',
};

export function isPanelOperation(value: unknown): value is PanelOperation {
  return typeof value === 'string' && (PANEL_OPERATIONS as readonly string[]).includes(value);
}

export function getPanelOperationLabel(operation: string): string {
  return PANEL_OPERATION_LABELS[operation as PanelOperation] ?? operation;
}

/**
 * Descrição do item de orçamento que a avaliação cria — "Reparar — Porta
 * dianteira esquerda". É o texto que o cliente lê no orçamento e no app, e que
 * o painel e o PDF mostram; `panel` e `operation` viajam junto no item para a
 * tela agrupar por peça (F3.3) sem ter de interpretar a descrição.
 */
export function panelItemDescription(panel: VehiclePanel, operation: PanelOperation): string {
  return `${PANEL_OPERATION_LABELS[operation]} — ${PANEL_LABELS[panel]}`;
}

/* ==========================================================================
 * Procedência da peça (F3.3) — o `partOrigin` de D4
 * ========================================================================== */

/**
 * De onde vem a peça que entra no carro, como o cliente e a seguradora
 * perguntam: genuína (da montadora), paralela, usada ou recondicionada. É a
 * informação que o cliente mais questiona num orçamento de colisão, e o
 * sinistro exige.
 *
 * "Procedência", e não "origem", na tela: o item de peça já tem `origin`
 * (estoque, fornecedor, o cliente traz), que diz de onde a OFICINA tira a peça
 * — logística interna, que o cliente não vê. São perguntas diferentes, e dois
 * campos "Origem" no mesmo formulário confundiriam as duas.
 *
 * Chave gravada no item (`item.partOrigin`), como a peça e o serviço.
 */
export const PART_ORIGINS = ['genuina', 'paralela', 'usada', 'recondicionada'] as const;

export type PartOrigin = (typeof PART_ORIGINS)[number];

const PART_ORIGIN_LABELS: Record<PartOrigin, string> = {
  genuina: 'Genuína',
  paralela: 'Paralela',
  usada: 'Usada',
  recondicionada: 'Recondicionada',
};

export function isPartOrigin(value: unknown): value is PartOrigin {
  return typeof value === 'string' && (PART_ORIGINS as readonly string[]).includes(value);
}

/** Rótulo da procedência, ou `null` para chave desconhecida — nunca a chave crua. */
export function getPartOriginLabel(origin: unknown): string | null {
  return isPartOrigin(origin) ? PART_ORIGIN_LABELS[origin] : null;
}
